import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../api";
import "./audioSection.css";

const PAGE_SIZE = 5;

// Static waveform bar heights (mirrors the design mock).
const WAVE_BARS = [
  10, 18, 30, 22, 44, 60, 38, 72, 90, 64, 48, 80, 100, 76, 52, 34, 58, 86, 70,
  42, 26, 48, 74, 96, 68, 50, 82, 60, 36, 54, 88, 72, 46, 28, 40, 66, 92, 78,
  56, 32, 44, 70, 94, 62, 38, 24, 50, 84, 68, 46, 60, 36, 20, 30, 16,
];

async function fetchAudioLectures() {
  const { data } = await api.get(
    `/audio-lectures?type=Lecture&limit=${PAGE_SIZE}&_=${Date.now()}`,
  );

  const isOk = data?.ok === true || data?.success === true;
  if (!isOk) throw new Error(data?.message || "API request failed");

  return data;
}

function formatDate(dateValue) {
  if (!dateValue) return "";
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return "";

  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatMinutes(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return "";
  const mins = Math.floor(Number(seconds) / 60);
  return `${mins} min${mins !== 1 ? "s" : ""}`;
}

function normalize(items) {
  return (items || []).map((x, idx) => {
    const lectureDate =
      x?.date ||
      x?.createdAt ||
      x?.updatedAt ||
      x?.publishedAt ||
      x?.uploadedAt;

    return {
      id: x?._id || x?.audioLectureId || x?.id || String(idx + 1),
      titleEn: x?.titleEn || x?.title || "",
      titleUr: x?.titleUr || "",
      description: x?.descriptionUr || x?.description || "",
      date: formatDate(lectureDate),
      category: x?.category?.name || x?.category || x?.categoryName || "",
      duration: formatMinutes(x?.duration),
      audio: x?.audioUrl || x?.url || x?.audio || "",
    };
  });
}

export default function AudioLectures() {
  const navigate = useNavigate();

  const [lectures, setLectures] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        const data = await fetchAudioLectures();
        const rawItems = data.items || data.data || data.audioLectures || [];
        const items = normalize(rawItems).slice(0, PAGE_SIZE);

        if (!alive) return;
        setLectures(items);
      } catch (err) {
        console.error("Audio lectures fetch failed:", err);
        if (!alive) return;
        setLectures([]);
      }
    }

    load();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const featuredLecture = lectures[0] || null;

  const restLectures = useMemo(() => {
    const rest = lectures.slice(1);
    return isMobile ? rest.slice(0, 2) : rest.slice(0, 4);
  }, [lectures, isMobile]);

  const goToDetail = (lecture) => {
    if (!lecture?.id) return;
    navigate(`/audio/${lecture.id}`);
  };

  return (
    <section className="home-audio">
      <div className="ha-head">
        <h2 className="ha-title">Audio Lectures</h2>
        <a className="ha-viewall" href="/audio">
          View All
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </a>
      </div>

      <div className="ha-media">
        {/* ===== Featured card (left) ===== */}
        <article
          className="ha-feat"
          onClick={() => featuredLecture && goToDetail(featuredLecture)}
        >
          <div className="ha-plate">
            <span className="ha-ar">دینی بیان</span>
            <div className="ha-wave" role="img" aria-label="Audio waveform">
              {WAVE_BARS.map((h, i) => (
                <span
                  key={i}
                  className="ha-wave-bar"
                  style={{
                    height: `${h}%`,
                    animationDelay: `${(i * 0.045).toFixed(2)}s`,
                  }}
                />
              ))}
            </div>
            <span className="ha-wave-time">
              {featuredLecture?.duration || ""}
            </span>
          </div>

          <div className="ha-body">
            {featuredLecture?.titleUr ? (
              <>
                <h3 className="ha-feat-title-ur" lang="ur" dir="rtl">
                  {featuredLecture.titleUr}
                </h3>
                {featuredLecture?.titleEn && (
                  <p className="ha-feat-title-en">{featuredLecture.titleEn}</p>
                )}
              </>
            ) : (
              <h3 className="ha-feat-title">{featuredLecture?.titleEn || ""}</h3>
            )}
            <p className="ha-feat-meta">
              {featuredLecture?.date || ""}
              {featuredLecture?.duration
                ? ` · ${featuredLecture.duration}`
                : ""}
            </p>
            {featuredLecture?.description && (
              <p className="ha-feat-sub" dir="auto">
                {featuredLecture.description}
              </p>
            )}
            <button
              type="button"
              className="ha-btn"
              onClick={(e) => {
                e.stopPropagation();
                if (featuredLecture) goToDetail(featuredLecture);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
              Listen now
            </button>
          </div>
        </article>
        {/* ===== End featured card ===== */}

        {/* ===== List (right) ===== */}
        <ul className="ha-list">
          {restLectures.map((lec, idx) => (
            <li key={lec.id} className="ha-row" onClick={() => goToDetail(lec)}>
              <span className="ha-num">
                {String(idx + 1).padStart(2, "0")}
              </span>
              <div className="ha-row-main">
                {lec.titleUr ? (
                  <>
                    <p className="ha-row-title-ur" lang="ur" dir="rtl">
                      {lec.titleUr}
                    </p>
                    {lec.titleEn && (
                      <p className="ha-row-title-en">{lec.titleEn}</p>
                    )}
                  </>
                ) : (
                  <p className="ha-row-title">{lec.titleEn}</p>
                )}
                <p className="ha-row-sub">
                  {lec.category ? `${lec.category} · ${lec.date}` : lec.date}
                </p>
              </div>
              <span className="ha-row-dur">{lec.duration}</span>
            </li>
          ))}
        </ul>
        {/* ===== End list ===== */}
      </div>
    </section>
  );
}
